import { ContinuitySchema, PersonSchema, ItemSchema, ROLES, type Continuity, type Person, type Item, type Allocation, type Snapshot, type Zone } from '@dissidium/contracts';
import { canonical, freeze } from '../kernel/primitives.ts';
import { OPENING_DATE, FIRST_EMBER, returnTransition } from './calendar.ts';

function fail(message: string): never { throw new Error(message); }
function unique(values: string[], label: string): void {
  if (new Set(values).size !== values.length) fail(`Duplicate ${label}`);
}
export function emptyContinuity(namespace: Continuity['namespace'] = 'test', chronologyId = 'chronology:bench'): Continuity {
  return { version: 1, chronologyId, namespace, closedRuns: [], persons: {}, remains: {}, items: {}, legacyIds: [], supportingIds: [], observations: [], echoes: [], pendingBequest: null, settlement: 'folded', date: { ...OPENING_DATE } };
}
export function validateLedger(input: unknown): Continuity {
  const c = ContinuitySchema.parse(input);
  unique(Object.values(c.persons).map(p => p.lineageKey), 'person lineage; renamed people still have one identity');
  unique(Object.values(c.persons).map(p => p.presentationKey), 'person presentation');
  unique(Object.values(c.items).map(i => i.provenanceKey), 'item provenance; one object cannot occupy two places');
  unique([...c.legacyIds, ...c.supportingIds], 'survivor registration');
  unique(c.closedRuns.map(r => r.id), 'closed run');
  unique(c.closedRuns.map(r => String(r.ordinal)), 'run ordinal');
  unique(c.observations.map(o => o.id), 'observation record');
  unique(c.echoes.map(e => e.id), 'echo record');
  for (const [key, p] of Object.entries(c.persons)) {
    if (key !== p.id) fail('Person key does not match identity');
    if (p.current.hp > p.current.maxHp || (p.lifecycle === 'dead') !== (p.current.hp === 0) || (p.lifecycle === 'dead') !== (p.deathEventId !== null)) fail(`${p.current.name}: death and health disagree`);
    if (p.lifecycle === 'unmanifested' && p.location !== null) fail(`${p.current.name} is unmanifested and cannot occupy a zone`);
    unique(p.current.inventory, 'inventory item');
    unique(p.current.equipment, 'equipped item');
    unique(p.current.reagents, 'reagent');
    const actual = Object.values(c.items).filter(i => i.allocation.kind === 'person' && i.allocation.id === p.id).map(i => i.id).sort();
    if (canonical(actual) !== canonical([...p.current.inventory].sort())) fail(`${p.current.name}: inventory and item ownership disagree`);
    if ([...p.current.equipment, ...p.current.reagents].some(id => !actual.includes(id))) fail(`${p.current.name}: equipment or reagent is not owned`);
    if (p.lifecycle === 'dead' && actual.length) fail(`${p.current.name} is dead; possessions belong to their remains`);
  }
  unique(Object.values(c.remains).map(r => r.personId), 'remains for a person');
  for (const [key, r] of Object.entries(c.remains)) {
    if (key !== r.id || c.persons[r.personId]?.lifecycle !== 'dead') fail('Remains must belong to one permanently dead person');
    if (r.fixedBurial && r.disposition !== 'buried' && r.disposition !== 'destroyed') fail('A fixed burial cannot be moved into corpse-work');
  }
  for (const [key, item] of Object.entries(c.items)) {
    if (key !== item.id) fail('Item key does not match identity');
    const a = item.allocation;
    if (a.kind === 'person' && (!c.persons[a.id] || c.persons[a.id]?.lifecycle === 'dead')) fail('Item owner must be alive');
    if (a.kind === 'remains' && (!c.remains[a.id] || c.remains[a.id]?.disposition === 'destroyed')) fail('Item cannot be allocated to missing or destroyed remains');
    if (a.kind === 'bequest' && (a.id !== c.chronologyId || c.pendingBequest !== item.id)) fail('Unexpected second bequest allocation');
    if (item.kind !== 'container' && item.contents.length) fail('Only a container has contents');
    unique(item.contents, 'container item');
    for (const child of item.contents) {
      if (child === item.id || c.items[child]?.kind === 'container' || !c.items[child] || canonical(c.items[child]?.allocation) !== canonical(a)) fail('Container contents must be distinct, co-located physical items');
    }
  }
  unique(Object.values(c.items).flatMap(i => i.contents), 'container membership');
  if (c.pendingBequest && (c.items[c.pendingBequest]?.allocation.kind !== 'bequest' || c.items[c.pendingBequest]?.units !== 1)) fail('The pending bequest must be one physical unit');
  for (const id of [...c.legacyIds, ...c.supportingIds]) if (!c.persons[id]?.original) fail('Survivor registration requires a promotion audit');
  for (const r of c.closedRuns) {
    if (r.chronologyId !== c.chronologyId || r.namespace !== c.namespace || !c.persons[r.pcId]) fail('Closed-run provenance does not belong to this chronology');
    unique(r.actions.map(a => a.id), 'verification action');
  }
  if (c.settlement === 'settled' && canonical(c.date) !== canonical(FIRST_EMBER)) fail('Settlement begins on 1 Ember 641 AC');
  return c;
}
/** Validate before and after a transaction; a thrown operation never mutates input. */
function transaction(c: Continuity, edit: (next: Continuity) => void): Continuity {
  const next = validateLedger(c);
  edit(next);
  assertContinuityTransition(c, next);
  return validateLedger(next);
}
export function registerPerson(c: Continuity, input: unknown): Continuity {
  const person = PersonSchema.parse(input);
  if (c.persons[person.id]) fail('That person already exists; a seed cannot replace their life');
  return transaction(c, next => { next.persons[person.id] = person; });
}
export function allocateItem(c: Continuity, input: unknown): Continuity {
  const item = ItemSchema.parse(input);
  if (c.items[item.id]) fail(`${item.name} already has an owner or location. A second copy was rejected.`);
  return transaction(c, next => {
    next.items[item.id] = item;
    if (item.allocation.kind === 'person') next.persons[item.allocation.id]?.current.inventory.push(item.id);
  });
}
function move(next: Continuity, id: string, from: Allocation, to: Allocation): void {
  const item = next.items[id] ?? fail('Unknown item');
  if (canonical(item.allocation) !== canonical(from)) fail(`${item.name} is not at the claimed source; transfer rejected`);
  if (from.kind === 'consumed') fail('A consumed or destroyed object cannot return');
  if (item.contents.length) fail('Empty the container before transferring it');
  for (const container of Object.values(next.items)) container.contents = container.contents.filter(child => child !== id);
  if (from.kind === 'person') {
    const p = next.persons[from.id] ?? fail('Unknown previous owner');
    p.current.inventory = p.current.inventory.filter(value => value !== id);
    p.current.equipment = p.current.equipment.filter(value => value !== id);
    p.current.reagents = p.current.reagents.filter(value => value !== id);
  }
  if (to.kind === 'person') {
    const p = next.persons[to.id] ?? fail('Unknown recipient');
    if (p.lifecycle === 'dead') fail(`${p.current.name} is dead and cannot receive an item`);
    p.current.inventory.push(id);
  }
  if (from.kind === 'bequest') next.pendingBequest = null;
  item.allocation = to;
}
export function transferItem(c: Continuity, id: string, from: Allocation, to: Allocation): Continuity {
  if (to.kind === 'bequest') fail('Use the single-bequest transaction');
  return transaction(c, next => { move(next, id, from, to); });
}
export function bequeath(c: Continuity, id: string, pcId: string): Continuity {
  if (c.settlement === 'settled') fail('A settled chronology has no receiving hollow');
  if (c.pendingBequest) fail('Only one bequest is permitted');
  const item = c.items[id] ?? fail('Unknown bequest');
  const a = item.allocation;
  if (!(a.kind === 'person' && a.id === pcId) && !(a.kind === 'remains' && c.remains[a.id]?.personId === pcId)) fail('The bequest must come from the ending PC or their remains');
  if (!['weapon', 'shield', 'worn', 'tool', 'ritual-dose', 'container'].includes(item.kind) || item.contents.length || item.units !== 1) fail('Bequeath one physical unit; empty containers carry no contents, currency and rights cannot pass');
  return transaction(c, next => { next.pendingBequest = id; move(next, id, a, { kind: 'bequest', id: next.chronologyId }); });
}
export function killPerson(c: Continuity, id: string, eventId: string): Continuity {
  return transaction(c, next => {
    const p = next.persons[id] ?? fail('Unknown person');
    if (p.lifecycle === 'dead') fail(`${p.current.name} has already died`);
    if (!p.location) fail('An unmanifested person cannot be killed off-screen');
    const remainsId = `remains:${id.slice('person:'.length)}`;
    next.remains[remainsId] = { id: remainsId, personId: id, disposition: 'exposed', location: p.location, fixedBurial: false };
    for (const item of Object.values(next.items)) if (item.allocation.kind === 'person' && item.allocation.id === id) item.allocation = { kind: 'remains', id: remainsId };
    p.lifecycle = 'dead'; p.deathEventId = eventId; p.current.hp = 0;
    p.current.inventory = []; p.current.equipment = []; p.current.reagents = [];
  });
}
export function bindLiving(c: Continuity, id: string, role: typeof ROLES[number], zone: Zone): Continuity {
  if (!ROLES.includes(role)) fail('Remains cannot become a living person or companion');
  return transaction(c, next => {
    const p = next.persons[id] ?? fail('Unknown living identity');
    if (p.lifecycle === 'dead') fail(`${p.current.name} died permanently. The ${role} role needs a distinct successor.`);
    if (p.location && p.location !== zone) fail(`${p.current.name} already occupies ${p.location}; a second body was rejected`);
    p.lifecycle = 'active'; p.location = zone;
  });
}
export function demanifest(c: Continuity, id: string): Continuity {
  return transaction(c, next => {
    const p = next.persons[id] ?? fail('Unknown survivor');
    if (!p.original || p.lifecycle === 'dead') fail('Only living coherent survivors can become unmanifested');
    p.lifecycle = 'unmanifested'; p.location = null;
  });
}
export function promote(c: Continuity, id: string, kind: 'legacy' | 'supporting'): Continuity {
  return transaction(c, next => {
    const p = next.persons[id] ?? fail('Unknown survivor');
    if (p.lifecycle === 'dead') fail('A dead person cannot be promoted');
    if (!p.location || !['Z04', 'Z11', 'Z12'].includes(p.location)) fail('A living departure must reach a still place');
    if (!p.original) p.original = structuredClone(p.current);
    const registry = kind === 'legacy' ? next.legacyIds : next.supportingIds;
    if (!registry.includes(id)) registry.push(id);
    p.lifecycle = 'unmanifested'; p.location = null;
  });
}
export function reviseSurvivor(c: Continuity, id: string, event: { id: string; skill: string; oath: string; allegiance: Snapshot['allegiances'][number] }): Continuity {
  return transaction(c, next => {
    const p = next.persons[id] ?? fail('Unknown person');
    if (p.lifecycle !== 'active' || p.current.deeds.includes(event.id)) fail('A new played event requires one active living person');
    p.current.skills.push(event.skill); p.current.oaths.push(event.oath);
    p.current.allegiances = [event.allegiance]; p.current.deeds.push(event.id);
  });
}
export function changeRemains(c: Continuity, id: string, disposition: Continuity['remains'][string]['disposition']): Continuity {
  return transaction(c, next => {
    const r = next.remains[id] ?? fail('Unknown remains');
    if (r.disposition === 'destroyed') fail('Destroyed remains cannot be restored');
    if (r.fixedBurial && disposition !== 'destroyed') fail('This burial has a fixed disposition');
    r.disposition = disposition;
    if (disposition === 'buried') r.fixedBurial = true;
    if (disposition === 'destroyed') for (const item of Object.values(next.items)) if (item.allocation.kind === 'remains' && item.allocation.id === id) item.allocation = { kind: 'location', id: `${r.location}:remains-site` };
  });
}
export function settlementAccount(c: Continuity): Readonly<{ living: Person[]; dead: string[]; items: Item[] }> {
  const valid = validateLedger(c);
  const ids = [...valid.legacyIds, ...valid.supportingIds].sort();
  const living = ids.map(id => valid.persons[id]!).filter(p => p.lifecycle !== 'dead');
  return freeze({ living, dead: Object.values(valid.persons).filter(p => p.lifecycle === 'dead').map(p => p.id).sort(), items: Object.values(valid.items).sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0) });
}
export function assertCompleteAccount(c: Continuity, candidate: ReturnType<typeof settlementAccount>): void {
  if (canonical(candidate) !== canonical(settlementAccount(c))) fail('The account is incomplete or changed: include every living survivor, including enemies, with their current possessions. The dead remain dead.');
}
export function nextRunOrdinal(c: Continuity): number {
  validateLedger(c);
  if (!returnTransition(c.settlement)) fail('This chronology is settled. There is no next run.');
  return c.closedRuns.length + 1;
}

/** Validate a new checkpoint against the prior authority, including tombstones. */
export function assertContinuityTransition(before: Continuity, after: Continuity): void {
  validateLedger(before); validateLedger(after);
  if (before.chronologyId !== after.chronologyId || before.namespace !== after.namespace) fail('A transition cannot replace its chronology');
  if (before.settlement === 'settled' && after.settlement !== 'settled') fail('Settlement is irreversible');
  for (const p of Object.values(before.persons)) {
    const next = after.persons[p.id];
    if (!next || next.lineageKey !== p.lineageKey || next.presentationKey !== p.presentationKey || canonical(next.kinshipKeys) !== canonical(p.kinshipKeys)) fail('A lived identity and its fixed kinships cannot be erased or renamed into a substitute');
    if (p.lifecycle === 'dead' && (next.lifecycle !== 'dead' || next.deathEventId !== p.deathEventId)) fail(`${p.current.name} died permanently; resurrection rejected`);
    if (p.original && canonical(p.original) !== canonical(next.original)) fail('The original promotion audit is immutable');
    if (p.original && next.lifecycle === 'unmanifested' && canonical(p.current) !== canonical(next.current)) fail('An unmanifested survivor must retain their current skills, possessions, health and allegiances');
  }
  for (const i of Object.values(before.items)) {
    const next = after.items[i.id];
    if (!next || next.provenanceKey !== i.provenanceKey || next.kind !== i.kind || next.units !== i.units) fail('A physical object cannot disappear or acquire extra units');
    if (i.allocation.kind === 'consumed' && canonical(i.allocation) !== canonical(next.allocation)) fail('A consumed object cannot be recreated');
  }
  for (const r of Object.values(before.remains)) {
    const next = after.remains[r.id];
    if (!next || next.personId !== r.personId) fail('Remains identity is permanent');
    if (r.disposition === 'destroyed' && next.disposition !== 'destroyed') fail('Destroyed remains cannot return');
    if (r.fixedBurial && (!next.fixedBurial || next.location !== r.location || !['buried', 'destroyed'].includes(next.disposition))) fail('An established burial cannot be relocated or animated');
  }
  for (const key of ['legacyIds', 'supportingIds'] as const) if (before[key].some(id => !after[key].includes(id))) fail('Survivor registries retain closed identities');
  for (const key of ['closedRuns', 'observations', 'echoes'] as const) if (canonical(before[key]) !== canonical(after[key].slice(0, before[key].length))) fail(`${key} retain immutable history`);
}
